import { DateTime } from 'luxon';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mixed, number, object } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Arquivo, Botao, Selecao } from 'Component/Form';
import Imagem from 'Component/Imagem';
import LayoutDefault from 'Component/Layout';
import RelogioDePonto from 'Interface/RelogiosDePonto';
import { ANOS, MESES, PDF } from 'View/constants';
import {
  useEditarRelogioDePontoMutation,
  useRecuperarRelogioDePontoQuery,
} from 'View/Service/Default/RelogiosDePonto';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_id: number().required('Obrigatório'),
  mes: number().required('Obrigatório'),
  ano: number().required('Obrigatório'),
  url: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return PDF.includes(valor.type);
      }
      return true;
    })
    .optional(),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { usuarioId = 0, id = 0 } = useParams();
  const [editarRelogio] = useEditarRelogioDePontoMutation();

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const { data: { relogio_de_ponto } = {} } =
    useRecuperarRelogioDePontoQuery(id);

  const enviar = async (dados: RelogioDePonto): Promise<void> => {
    try {
      await editarRelogio(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editando arquivo de relogio de ponto"
        descricao="Preencha o formulário e clique em enviar para editar o arquivo"
      />
      <Form
        onSubmit={enviar}
        regras={regras}
        inicial={relogio_de_ponto}
        desabilitado={relogio_de_ponto?.assinado as unknown as boolean}
      >
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="bg-white shadow-sm rounded p-4">
              {relogio_de_ponto?.assinado == 1 && (
                <p className="text-info">
                  Não é possível editar um relógio de ponto já assinado
                </p>
              )}
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="mes"
                    rotulo="Mês"
                    opcoes={MESES}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="ano"
                    rotulo="ano"
                    opcoes={ANOS}
                  />
                </div>
                <div className="col-md-12">
                  <Arquivo name="url" />
                  <a
                    href={relogio_de_ponto?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none d-block mt-n3"
                  >
                    Abrir arquivo do relógio de ponto
                  </a>
                </div>
                <div className="col-md-12">
                  {relogio_de_ponto?.assinatura && (
                    <Imagem
                      src={relogio_de_ponto.assinatura}
                      className="img-fluid"
                    />
                  )}
                  {relogio_de_ponto?.data_da_assinatura && (
                    <p>
                      Assinado em{' '}
                      {relogio_de_ponto.data_da_assinatura
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Editar;
